import { useCallback, useEffect, useState } from "react";
import API from "@/lib/api";
import { HardDrive, Download, ShieldCheck, AlertTriangle, KeyRound, Activity, CheckCircle2, XCircle, Clock } from "lucide-react";

/**
 * BackupCard — pull-only DB backup control for admins.
 *
 * Shows three things:
 *   1) Tripwire status  — last successful pull + hours-since + green
 *      or amber pill when we're outside the 36-hour window.
 *   2) Rehearsal status — last decrypt-and-parse drill, with a warning
 *      pill after 30 days so the operator is nudged monthly.
 *   3) Actions          — passphrase input → Download or Rehearse.
 *
 * All three share the same passphrase state; the field clears after
 * either action succeeds so a wandering tab can't leak it.
 */
export default function BackupCard() {
  const [status, setStatus] = useState(null); // null=loading, {} once loaded
  const [passphrase, setPassphrase] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [rehearsing, setRehearsing] = useState(false);
  const [error, setError] = useState("");
  const [lastDownloadName, setLastDownloadName] = useState("");
  const [rehearsalResult, setRehearsalResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/backup/status");
      setStatus(data || {});
    } catch {
      setStatus({});
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function runBackup() {
    setError("");
    if (!passphrase) { setError("Enter the backup passphrase."); return; }
    setDownloading(true);
    try {
      const res = await API.post("/admin/backup/download", { passphrase }, { responseType: "blob" });
      const fromHeader = res.headers?.["x-backup-filename"] || "";
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = fromHeader || (match && match[1]) || `glpflo-backup-${Date.now()}.tar.gz.enc`;
      const blob = new Blob([res.data], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setLastDownloadName(filename);
      setPassphrase("");
      load();
    } catch (e) {
      setError(await extractError(e));
    } finally {
      setDownloading(false);
    }
  }

  async function runRehearsal() {
    setError("");
    setRehearsalResult(null);
    if (!passphrase) { setError("Enter the backup passphrase."); return; }
    setRehearsing(true);
    try {
      const { data } = await API.post("/admin/backup/rehearsal", { passphrase });
      setRehearsalResult(data);
      setPassphrase("");
      load();
    } catch (e) {
      setError(await extractError(e));
    } finally {
      setRehearsing(false);
    }
  }

  const configured = !!status?.configured;
  const hoursSince = status?.hours_since_last;
  const healthy    = !!status?.healthy;
  const tripwireHours = status?.tripwire_hours || 36;
  const lastRehearsal = status?.last_rehearsal;
  const lastRehearsalDate = lastRehearsal?.started_at ? new Date(lastRehearsal.started_at) : null;
  const rehearsalDaysAgo = lastRehearsalDate ? Math.floor((Date.now() - lastRehearsalDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
  const rehearsalStale   = rehearsalDaysAgo == null || rehearsalDaysAgo >= 30;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="admin-backup-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
          <HardDrive className="w-5 h-5 text-emerald-600 dark:text-emerald-300" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Encrypted DB Backup
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Pull-only · AES-256 · openssl-compatible · nothing retained server-side
          </p>
        </div>
      </div>

      {status && configured === false && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200" data-testid="admin-backup-unconfigured">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Not configured yet.</div>
            <div className="text-xs">
              Add <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">BACKUP_PASSPHRASE</code> to the backend <code>.env</code> and restart the backend. Then paste the same passphrase below to pull a backup.
            </div>
          </div>
        </div>
      )}

      {configured && (
        <>
          {/* Status pills — tripwire + rehearsal at a glance. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            <StatusPill
              testid="admin-backup-tripwire-pill"
              icon={healthy ? CheckCircle2 : AlertTriangle}
              tone={healthy ? "ok" : "warn"}
              label={`Last pull ${hoursSince == null ? "never" : `${hoursSince}h ago`}`}
              detail={healthy ? `within ${tripwireHours}h window` : `overdue · tripwire ≥${tripwireHours}h`}
            />
            <StatusPill
              testid="admin-backup-rehearsal-pill"
              icon={rehearsalStale ? AlertTriangle : CheckCircle2}
              tone={rehearsalStale ? "warn" : "ok"}
              label={`Last rehearsal ${rehearsalDaysAgo == null ? "never" : `${rehearsalDaysAgo}d ago`}`}
              detail={rehearsalStale ? "run monthly to prove the passphrase" : "restore drill fresh"}
            />
          </div>

          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1 mb-1">
            <KeyRound className="w-3.5 h-3.5" /> Backup passphrase
          </label>
          <input
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Must match BACKUP_PASSPHRASE from backend .env"
            autoComplete="off"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-emerald-400 focus:outline-none"
            data-testid="admin-backup-passphrase-input"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={runBackup}
              disabled={downloading || rehearsing || !passphrase}
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold text-sm py-2.5 flex items-center justify-center gap-2 transition-colors"
              data-testid="admin-backup-download-btn"
            >
              <Download className={`w-4 h-4 ${downloading ? "animate-pulse" : ""}`} />
              {downloading ? "Downloading…" : "Download backup"}
            </button>
            <button
              type="button"
              onClick={runRehearsal}
              disabled={downloading || rehearsing || !passphrase}
              className="rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 text-slate-900 dark:text-white font-semibold text-sm py-2.5 flex items-center justify-center gap-2 border border-slate-300 dark:border-slate-700 transition-colors"
              data-testid="admin-backup-rehearsal-btn"
            >
              <Activity className={`w-4 h-4 ${rehearsing ? "animate-pulse" : ""}`} />
              {rehearsing ? "Rehearsing…" : "Run restore rehearsal"}
            </button>
          </div>

          {error && (
            <div className="mt-2 text-xs text-rose-600 dark:text-rose-400" data-testid="admin-backup-error">
              {error}
            </div>
          )}
          {lastDownloadName && (
            <div className="mt-2 text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1" data-testid="admin-backup-success">
              <ShieldCheck className="w-3.5 h-3.5" /> Saved <code className="bg-emerald-50 dark:bg-emerald-900/30 px-1 rounded">{lastDownloadName}</code>
            </div>
          )}
          {rehearsalResult && (
            <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3 text-xs" data-testid="admin-backup-rehearsal-result">
              <div className={`flex items-center gap-1 font-semibold mb-1 ${rehearsalResult.ok ? "text-emerald-700 dark:text-emerald-300" : "text-rose-600 dark:text-rose-400"}`}>
                {rehearsalResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {rehearsalResult.ok ? "Rehearsal passed" : "Rehearsal failed"}
                <span className="ml-auto flex items-center gap-1 text-slate-500 dark:text-slate-400 font-normal">
                  <Clock className="w-3 h-3" /> {rehearsalResult.duration_ms}ms
                </span>
              </div>
              <ul className="space-y-0.5">
                {(rehearsalResult.steps || []).map((s, i) => (
                  <li key={i} className={`flex items-center gap-1 ${s.ok ? "text-slate-700 dark:text-slate-300" : "text-rose-600 dark:text-rose-400"}`}>
                    {s.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                    <code className="text-[11px]">{s.name}</code>
                    {s.detail && <span className="text-slate-500 dark:text-slate-400 text-[11px]">· {s.detail}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="mt-3 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
            Decrypt on your PC with the bundled <code>glpflo-restore.ps1</code> or with{" "}
            <code>openssl enc -aes-256-cbc -pbkdf2 -d -salt -in &lt;file&gt; -out backup.tar.gz -pass pass:&apos;&lt;passphrase&gt;&apos;</code>. Then <code>mongorestore --drop dump/</code>.
            The tripwire runs hourly and will email every admin if no successful pull is logged in {tripwireHours}h.
          </p>
        </>
      )}
    </div>
  );
}

function StatusPill({ icon: Icon, tone, label, detail, testid }) {
  const tones = {
    ok:   "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200",
    warn: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200",
  };
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tones[tone]}`} data-testid={testid}>
      <Icon className="w-4 h-4 shrink-0" />
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate">{label}</div>
        <div className="text-[11px] opacity-80 truncate">{detail}</div>
      </div>
    </div>
  );
}

async function extractError(e) {
  const body = e?.response?.data;
  if (body instanceof Blob) {
    try {
      const txt = await body.text();
      return (JSON.parse(txt)?.detail) || e?.message || "Request failed";
    } catch {
      return e?.message || "Request failed";
    }
  }
  return body?.detail || e?.message || "Request failed";
}
